import { useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Modal, Snackbar } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import { StyledDataTable } from "@/components/molecules/StyledDataTable";
import { MONETA_API_BASE_URL } from "@/config";
import {
  CallFlowVersion,
  CallPlanInfoFragment,
  CallScheduleInfoFragment,
  type CreateCallScheduleMutation,
  type CreateCallScheduleMutationVariables,
  DeleteCallScheduleMutation,
  DeleteCallScheduleMutationVariables,
  type GetDeletedCallSchedulesQuery,
  type RestoreCallScheduleMutation,
  type RestoreCallScheduleMutationVariables,
} from "@/graphql/graphql";
import useFlag from "@/hooks/useFlag";
import { CREATE_CALL_SCHEDULE, DELETE_CALL_SCHEDULE } from "@/pages/UserDetails/queries";
import { UserDisplayButton } from "@/styles/flex";
import FeatureFlags from "@/utils/featureFlags";

import { Playlist } from "../../../../types/api";
import { GET_DELETED_CALL_SCHEDULES, RESTORE_CALL_SCHEDULE } from "../../queries";
import { HeadSection, RightHeaderDiv, RightStyledH1 } from "../../styles";
import { PatientTimezoneDateBlurb } from "../CallPlans/PatientTimezoneDateBlurb";
import { CallPushedModal } from "./CallPushedNote";
import { getCallScheduleColumns, getDeletedCallScheduleColumns, sortCrons } from "./columns";
import { EditScheduleModal } from "./editScheduleModal";
import { InactivePlaylist } from "./InactivePlaylistCheck";
import { PushCallDialog, PushMaintenanceCallDialog } from "./PushCallModal";
import SchedulePicker from "./SchedulePicker/SchedulePicker";

type CallScheduleProps = {
  callSchedules: CallScheduleInfoFragment[];
  userId: number;
  timeZone?: string | null;
  playlists: Playlist[];
  callPlans: CallPlanInfoFragment[];
  inMaintenance?: boolean;
};

const commonTableStyles = { maxWidth: "700px" };

export const CallSchedules: React.FC<CallScheduleProps> = ({
  callSchedules,
  userId,
  timeZone,
  playlists,
  callPlans,
  inMaintenance,
}) => {
  const callPlanTabEnabled = useFlag(FeatureFlags.SHOW_CALL_PLAN_TAB);
  const [showError, setShowError] = useState<boolean>(false);
  const [editScheduleModal, setEditScheduleModal] = useState<boolean>(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState<boolean>(false);
  const [callPushModal, setcallPushModal] = useState<boolean>(false);
  const [createCallSchedule, { error: createCallScheduleError }] = useMutation<
    CreateCallScheduleMutation,
    CreateCallScheduleMutationVariables
  >(CREATE_CALL_SCHEDULE, { refetchQueries: ["GetSingleUser"] });
  const [selectedSchedule, setSelectedSchedule] = useState<CallScheduleInfoFragment | undefined>();
  const handleRowClicked = (row: CallScheduleInfoFragment) => {
    setSelectedSchedule(row);
    setEditScheduleModal(true);
  };

  const {
    data,
    error: getDeletedCallScheduleError,
    loading,
  } = useQuery<GetDeletedCallSchedulesQuery>(GET_DELETED_CALL_SCHEDULES, {
    variables: { userId },
    fetchPolicy: "cache-and-network",
  });
  const sortedDeletedCallSchedules = data?.deletedCallSchedules
    ? [...data.deletedCallSchedules].sort((a, b) => sortCrons(a.cronExpression, b.cronExpression))
    : [];

  const [deleteCallSchedule, { error: deleteCallScheduleError }] = useMutation<
    DeleteCallScheduleMutation,
    DeleteCallScheduleMutationVariables
  >(DELETE_CALL_SCHEDULE, { refetchQueries: ["GetSingleUser", "GetDeletedCallSchedules"] });
  const [restoreCallSchedule, { error: restoreCallScheduleError }] = useMutation<
    RestoreCallScheduleMutation,
    RestoreCallScheduleMutationVariables
  >(RESTORE_CALL_SCHEDULE, { refetchQueries: ["GetSingleUser", "GetDeletedCallSchedules"] });

  const onClickDelete = async (id: number) => {
    try {
      await deleteCallSchedule({ variables: { id } });
    } catch (error) {
      setShowError(true);
    }
  };
  const onClickRestore = async (id: number) => {
    try {
      await restoreCallSchedule({ variables: { id } });
    } catch (error) {
      setShowError(true);
    }
  };

  useEffect(() => {
    if (
      deleteCallScheduleError ||
      restoreCallScheduleError ||
      getDeletedCallScheduleError ||
      createCallScheduleError
    ) {
      setShowError(true);
    }
  }, [
    deleteCallScheduleError,
    restoreCallScheduleError,
    getDeletedCallScheduleError,
    createCallScheduleError,
  ]);

  const SinglePushCall = () => {
    axios.post(
      `${MONETA_API_BASE_URL}/call-flow${callPlanTabEnabled ? "" : "/v2"}/start`,
      {
        callScheduleId: null,
        accountId: userId,
      },
      {
        withCredentials: true,
      }
    );
    setConfirmSubmitOpen(false);
    setcallPushModal(true);
  };

  const onCreateCallSchedule = async (cron: string) => {
    try {
      await createCallSchedule({
        variables: {
          cronExpression: cron,
          accountId: userId,
          callFlowVersion: CallFlowVersion.V2,
        },
      });
    } catch (error) {
      setShowError(true);
    }
  };

  const hasActivePlaylist = playlists.some((item) => item.isActive);
  const hasActiveCallPlan = callPlanTabEnabled && callPlans.some((callPlan) => callPlan.isActive);
  const sortedCallSchedules = [...callSchedules].sort((a, b) =>
    sortCrons(a.cronExpression, b.cronExpression)
  );

  return (
    <div style={{ width: "100%" }}>
      <HeadSection>
        <RightHeaderDiv>
          <RightStyledH1>Call Schedules</RightStyledH1>
        </RightHeaderDiv>
        <UserDisplayButton
          onClick={() => {
            setConfirmSubmitOpen(true);
          }}
        >
          <Button>Push Call Now</Button>
        </UserDisplayButton>
      </HeadSection>
      <br />
      {timeZone && (
        <PatientTimezoneDateBlurb
          accountTimeZone={timeZone}
          alternateText="Schedules are in the patient's timezone: "
        />
      )}
      <div style={commonTableStyles}>
        <StyledDataTable
          columns={getCallScheduleColumns(onClickDelete)}
          data={sortedCallSchedules}
          onRowClicked={handleRowClicked}
        />
      </div>
      <br />
      <SchedulePicker onSubmit={onCreateCallSchedule} />
      <div style={{ width: "100%" }}>
        <RightHeaderDiv>
          <RightStyledH1>Deleted Call Schedules</RightStyledH1>
        </RightHeaderDiv>
      </div>
      <div style={commonTableStyles}>
        <StyledDataTable
          columns={getDeletedCallScheduleColumns(onClickRestore)}
          data={sortedDeletedCallSchedules}
          progressPending={loading}
        />
      </div>
      {editScheduleModal && (
        <EditScheduleModal
          trigger={editScheduleModal}
          setTrigger={setEditScheduleModal}
          callSchedule={selectedSchedule}
        />
      )}
      {confirmSubmitOpen &&
        (hasActiveCallPlan || hasActivePlaylist || inMaintenance ? (
          <ConfirmDialog
            confirmOpen={confirmSubmitOpen}
            dialogContent={inMaintenance ? PushMaintenanceCallDialog : PushCallDialog}
            onSubmit={SinglePushCall}
            onClose={() => setConfirmSubmitOpen(false)}
          />
        ) : (
          <Modal title="" open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)}>
            <InactivePlaylist onClose={() => setConfirmSubmitOpen(false)} />
          </Modal>
        ))}
      {callPushModal && (
        <Modal title="" open={callPushModal} onClose={() => setcallPushModal(false)}>
          <CallPushedModal onClose={() => setcallPushModal(false)} />
        </Modal>
      )}
      {showError && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={showError}
          onClose={() => setShowError(false)}
        >
          <Alert onClose={() => setShowError(false)} severity="error">
            {deleteCallScheduleError && "Failed to delete call schedule."}
            {restoreCallScheduleError && "Failed to restore call schedule."}
            {createCallScheduleError && "Failed to create call schedule."}
            {getDeletedCallScheduleError && "Failed to get deleted call schedule."}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};
